import {Component, computed, signal, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {forkJoin, of} from "rxjs";
import {
    Customer,
    DcOverride,
    DcOverrideIndicator,
    DistributionCenter,
    PartnerMapping,
    SaveDcOverridesRequest,
    SaveDcOverrideRow,
    SavePartnerMapRow,
    TradingPartner,
    ZaCode
} from "@app/_models/api";
import {ApiService} from "@app/_services/api.service";
import {GlobalService} from "@app/_services/global.service";
import {ToastService} from "@app/_services/toast.service";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {validateZaFormula} from "@app/_helpers/za-formula";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

// A code is passthrough (no stored row), "None" (a reported gap), or a formula (transform).
type CodeState = 'passthrough' | 'none' | 'formula';

// One row of the partner-map grid. Every ZA code has a row; only non-passthrough rows are persisted.
interface PartnerRow {
    zaCode: string;
    state: CodeState;
    formula: string;      // edited when state === 'formula'
    description: string;
    effFrDate: string;    // yyyy-mm-dd, '' = unset (server defaults the range)
    effToDate: string;
}

// Override editor row. 'inherit' means "follow the partner map" (not sent); anything else is an override.
type OverrideState = 'inherit' | 'passthrough' | 'none' | 'formula';

interface OverrideRow {
    zaCode: string;
    state: OverrideState;
    formula: string;
    description: string;
    effFrDate: string;
    effToDate: string;
}

@Component({
    selector: 'app-mappings',
    imports: [FormsModule, ModalConfirmComponent],
    templateUrl: './mappings.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './mappings.component.scss'
})
export class MappingsComponent {
    @ViewChild('overrideModal') private overrideModal!: TemplateRef<unknown>;
    @ViewChild('allOverridesModal') private allOverridesModal!: TemplateRef<unknown>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;

    overrideModalRef!: NgbModalRef;
    allOverridesModalRef!: NgbModalRef;

    customers: Customer[] = [];
    tradingPartners = signal<TradingPartner[]>([]);
    customerId = signal<string | undefined>(undefined);
    tradingPartnerId = signal<string | undefined>(undefined);

    private zaCodes: string[] = [];
    private loadedPartnerCodes = new Set<string>();      // codes that had a stored partner row on load

    partnerRows = signal<PartnerRow[]>([]);
    selectedCodes = signal<Set<string>>(new Set());
    overrideIndicators = signal<DcOverrideIndicator[]>([]);
    savingPartnerMap = signal(false);

    // The persisted shape of the grid as last loaded; unsaved edits are the rows that differ from it.
    private savedMapSnapshot = signal('[]');
    partnerMapDirty = computed(() => JSON.stringify(this.buildSaveRows()) !== this.savedMapSnapshot());

    // --- Override editor modal state ---
    private distributionCenters = signal<DistributionCenter[]>([]);
    selectedDcIds = signal<Set<string>>(new Set());
    overrideRows = signal<OverrideRow[]>([]);
    savingOverrides = signal(false);
    dcList = computed(() => this.distributionCenters());

    // --- All-overrides modal state ---
    allOverrides = signal<DcOverride[]>([]);

    selectedTradingPartnerName = computed(() =>
        this.tradingPartners().find(tp => tp.id === this.tradingPartnerId())?.name ?? '');

    constructor(
        public globalService: GlobalService,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private toastService: ToastService,
        private modalService: NgbModal
    ) {
        this.customers = this.route.snapshot.data['resolvedData'].data;
    }

    // --- Customer / trading-partner cascade ---

    onCustomerChange(event: Event) {
        const id = (event.target as HTMLSelectElement).value || undefined;
        this.customerId.set(id);
        this.tradingPartnerId.set(undefined);
        this.tradingPartners.set([]);
        this.resetGrid();

        if (!id) return;

        this.globalService.showWaitOverlay();
        this.apiService.getTradingPartners(id).subscribe({
            next: partners => {
                this.tradingPartners.set(partners);
                this.globalService.hideWaitOverlay();
            },
            error: () => {
                this.toastService.showDanger("Request Failed", "Failed to load trading partners.");
                this.globalService.hideWaitOverlay();
            }
        });
    }

    onTradingPartnerChange(event: Event) {
        const id = (event.target as HTMLSelectElement).value || undefined;
        this.tradingPartnerId.set(id);
        this.resetGrid();
        if (this.customerId() && id) this.loadPartnerMap();
    }

    private loadPartnerMap() {
        const customerId = this.customerId()!;
        const tradingPartnerId = this.tradingPartnerId()!;

        this.globalService.showWaitOverlay();
        forkJoin({
            zaCodes: this.apiService.getZaCodes(),
            partnerMap: this.apiService.getPartnerMap(customerId, tradingPartnerId),
            indicators: this.apiService.getDcOverrideIndicators(customerId, tradingPartnerId)
        }).subscribe({
            next: ({zaCodes, partnerMap, indicators}) => {
                this.zaCodes = zaCodes.map((z: ZaCode) => z.value).sort((a, b) => a.localeCompare(b));
                this.hydrateGrid(partnerMap);
                this.overrideIndicators.set(indicators);
                this.globalService.hideWaitOverlay();
            },
            error: () => {
                this.toastService.showDanger("Request Failed", "Failed to load the partner map.");
                this.globalService.hideWaitOverlay();
            }
        });
    }

    // Build the full grid: every ZA code starts passthrough, then overlay the sparse partner rows.
    private hydrateGrid(partnerMap: PartnerMapping[]) {
        const byCode = new Map(partnerMap.map(m => [m.zaCode, m]));
        this.loadedPartnerCodes = new Set(byCode.keys());

        this.partnerRows.set(this.zaCodes.map(zaCode => {
            const m = byCode.get(zaCode);
            if (!m) {
                return {zaCode, state: 'passthrough', formula: '', description: '', effFrDate: '', effToDate: ''};
            }
            return {
                zaCode,
                state: m.formula === 'None' ? 'none' : 'formula',
                formula: m.formula === 'None' ? '' : m.formula,
                description: m.description ?? '',
                effFrDate: this.toDateInput(m.effFrDate),
                effToDate: this.toDateInput(m.effToDate)
            };
        }));
        this.savedMapSnapshot.set(JSON.stringify(this.buildSaveRows()));
        this.selectedCodes.set(new Set());
    }

    private resetGrid() {
        this.partnerRows.set([]);
        this.savedMapSnapshot.set('[]');
        this.selectedCodes.set(new Set());
        this.overrideIndicators.set([]);
        this.loadedPartnerCodes = new Set();
    }

    // --- Partner grid editing ---

    setState(zaCode: string, state: CodeState) {
        this.partnerRows.set(this.partnerRows().map(r => r.zaCode !== zaCode ? r : {...r, state}));
    }

    setFormula(zaCode: string, formula: string) {
        this.partnerRows.set(this.partnerRows().map(r => r.zaCode !== zaCode ? r : {...r, formula}));
    }

    setEffFrDate(zaCode: string, value: string) {
        this.partnerRows.set(this.partnerRows().map(r => r.zaCode !== zaCode ? r : {...r, effFrDate: value}));
    }

    setEffToDate(zaCode: string, value: string) {
        this.partnerRows.set(this.partnerRows().map(r => r.zaCode !== zaCode ? r : {...r, effToDate: value}));
    }

    toggleCode(zaCode: string) {
        const next = new Set(this.selectedCodes());
        if (next.has(zaCode)) next.delete(zaCode); else next.add(zaCode);
        this.selectedCodes.set(next);
    }

    isAllCodesSelected(): boolean {
        const rows = this.partnerRows();
        return rows.length > 0 && rows.every(r => this.selectedCodes().has(r.zaCode));
    }

    toggleSelectAllCodes() {
        this.selectedCodes.set(this.isAllCodesSelected() ? new Set() : new Set(this.partnerRows().map(r => r.zaCode)));
    }

    bulkSet(state: CodeState) {
        const selected = this.selectedCodes();
        if (selected.size === 0) return;
        this.partnerRows.set(this.partnerRows().map(r => selected.has(r.zaCode) ? {...r, state} : r));
    }

    savePartnerMap() {
        const customerId = this.customerId();
        const tradingPartnerId = this.tradingPartnerId();
        if (!customerId || !tradingPartnerId) return;

        const invalid = this.partnerRows().find(r => r.state === 'formula' && !r.formula.trim());
        if (invalid) {
            this.toastService.showDanger("Invalid Data", `Enter a formula for ${invalid.zaCode} or set it to Passthrough / None.`);
            return;
        }

        const formulaError = this.partnerRows()
            .filter(r => r.state === 'formula')
            .map(r => validateZaFormula(r.formula.trim(), r.zaCode, this.zaCodes))
            .find(e => e !== null);
        if (formulaError) {
            this.toastService.showDanger("Invalid Formula", formulaError);
            return;
        }

        this.savingPartnerMap.set(true);
        this.apiService.savePartnerMap(customerId, tradingPartnerId, this.buildSaveRows()).subscribe({
            next: () => {
                this.toastService.showStandard("Success", "Partner map saved.");
                this.savingPartnerMap.set(false);
                this.loadPartnerMap();
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", e?.error?.detail || "Could not save the partner map.");
                this.savingPartnerMap.set(false);
                throw e;
            }
        });
    }

    private buildSaveRows(): SavePartnerMapRow[] {
        return this.partnerRows()
            .filter(r => r.state !== 'passthrough')
            .map(r => ({
                zaCode: r.zaCode,
                formula: r.state === 'none' ? 'None' : r.formula.trim(),
                description: r.description.trim() || null,
                effFrDate: r.effFrDate || null,
                effToDate: r.effToDate || null
            }));
    }

    reprocessPartnerFiles() {
        const customerId = this.customerId();
        const tradingPartnerId = this.tradingPartnerId();
        if (!customerId || !tradingPartnerId) return;

        this.globalService.showWaitOverlay('Assessing files...');
        this.apiService.count852FilesByPartner(customerId, tradingPartnerId).subscribe({
            next: ({fileCount}) => {
                this.globalService.hideWaitOverlay();
                if (fileCount === 0) {
                    this.toastService.showInfo("Nothing to Reprocess", "No 852 files found for this trading partner.");
                    return;
                }
                this.confirmReprocessPartnerFiles(customerId, tradingPartnerId, fileCount);
            },
            error: (e) => {
                this.globalService.hideWaitOverlay();
                this.toastService.showDanger("Request Failed",
                    e?.error?.detail || "Could not count the 852 files for this trading partner.");
                throw e;
            }
        });
    }

    private confirmReprocessPartnerFiles(customerId: string, tradingPartnerId: string, fileCount: number) {
        this.modalConfirmComponent.open(
            `Reprocess ${fileCount} 852 file(s)?`,
            `"${this.selectedTradingPartnerName()}" : large partners can take a while and delay other 852 processing`);
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                this.globalService.showWaitOverlay('Queuing reprocessing request...');
                this.apiService.reprocess852FilesByPartner(customerId, tradingPartnerId).subscribe({
                    next: response => {
                        this.globalService.hideWaitOverlay();
                        this.toastService.showStandard("Success", response.message);
                    },
                    error: (e) => {
                        this.globalService.hideWaitOverlay();
                        this.toastService.showDanger("Request Failed",
                            e?.error?.detail || "Could not queue the 852 files for reprocessing.");
                        throw e;
                    }
                });
            },
            () => {
            });
    }

    // --- Override editor ---

    openOverrideEditor(preselectDcId?: string) {
        const customerId = this.customerId();
        const tradingPartnerId = this.tradingPartnerId();
        if (!customerId || !tradingPartnerId) return;

        this.selectedDcIds.set(preselectDcId ? new Set([preselectDcId]) : new Set());
        this.overrideRows.set(this.zaCodes.map(zaCode => ({
            zaCode, state: 'inherit', formula: '', description: '', effFrDate: '', effToDate: ''
        })));

        this.globalService.showWaitOverlay();
        forkJoin({
            dcs: this.apiService.getDistributionCenters(customerId, tradingPartnerId),
            // Only a preselected DC has existing overrides to show; otherwise start with a clean grid.
            overrides: preselectDcId ? this.apiService.getAllDcOverrides(customerId) : of<DcOverride[]>([])
        }).subscribe({
            next: ({dcs, overrides}) => {
                this.distributionCenters.set(dcs.distributionCenters);
                if (preselectDcId) {
                    this.hydrateOverrideGrid(overrides.filter(o => o.distributionCenterId === preselectDcId));
                }
                this.globalService.hideWaitOverlay();
                this.overrideModalRef = this.modalService.open(this.overrideModal, {
                    modalDialogClass: "modal-dialog-centered modal-xl",
                    backdrop: "static"
                });
            },
            error: () => {
                this.toastService.showDanger("Request Failed", "Failed to load distribution centers.");
                this.globalService.hideWaitOverlay();
            }
        });
    }

    // Overlay a DC's stored overrides onto the all-inherit grid. A self-map (formula === zaCode) is a
    // forced passthrough at the DC; 'None' is a gap; anything else is a formula.
    private hydrateOverrideGrid(overrides: DcOverride[]) {
        const byCode = new Map(overrides.map(o => [o.zaCode, o]));
        this.overrideRows.set(this.overrideRows().map(row => {
            const o = byCode.get(row.zaCode);
            if (!o) return row;
            const state: OverrideState =
                o.formula === 'None' ? 'none' : o.formula === row.zaCode ? 'passthrough' : 'formula';
            return {
                zaCode: row.zaCode,
                state,
                formula: state === 'formula' ? o.formula : '',
                description: o.description ?? '',
                effFrDate: this.toDateInput(o.effFrDate),
                effToDate: this.toDateInput(o.effToDate)
            };
        }));
    }

    toggleDc(dcId: string) {
        const next = new Set(this.selectedDcIds());
        if (next.has(dcId)) next.delete(dcId); else next.add(dcId);
        this.selectedDcIds.set(next);
    }

    setOverrideState(zaCode: string, state: OverrideState) {
        this.overrideRows.set(this.overrideRows().map(r => r.zaCode !== zaCode ? r : {...r, state}));
    }

    setOverrideFormula(zaCode: string, formula: string) {
        this.overrideRows.set(this.overrideRows().map(r => r.zaCode !== zaCode ? r : {...r, formula}));
    }

    saveOverrides() {
        const customerId = this.customerId();
        if (!customerId) return;

        const dcIds = [...this.selectedDcIds()];
        if (dcIds.length === 0) {
            this.toastService.showDanger("No DCs Selected", "Select at least one distribution center.");
            return;
        }

        const edited = this.overrideRows().filter(r => r.state !== 'inherit');
        if (edited.length === 0) {
            this.toastService.showDanger("Nothing To Apply", "Set at least one code to apply as an override.");
            return;
        }

        const invalid = edited.find(r => r.state === 'formula' && !r.formula.trim());
        if (invalid) {
            this.toastService.showDanger("Invalid Data", `Enter a formula for ${invalid.zaCode}.`);
            return;
        }

        const formulaError = edited
            .filter(r => r.state === 'formula')
            .map(r => validateZaFormula(r.formula.trim(), r.zaCode, this.zaCodes))
            .find(e => e !== null);
        if (formulaError) {
            this.toastService.showDanger("Invalid Formula", formulaError);
            return;
        }

        const overrides: SaveDcOverrideRow[] = edited.map(r => ({
            zaCode: r.zaCode,
            // passthrough at a DC is a self-map (force passthrough where the partner default is None)
            formula: r.state === 'passthrough' ? r.zaCode : r.state === 'none' ? 'None' : r.formula.trim(),
            description: r.description.trim() || null,
            effFrDate: r.effFrDate || null,
            effToDate: r.effToDate || null
        }));

        const body: SaveDcOverridesRequest = {distributionCenterIds: dcIds, overrides};

        this.savingOverrides.set(true);
        this.apiService.saveDcOverrides(customerId, body).subscribe({
            next: () => {
                this.toastService.showStandard("Success", "Overrides applied.");
                this.savingOverrides.set(false);
                this.overrideModalRef.close();
                this.refreshIndicators();
            },
            error: (e) => {
                this.toastService.showDanger("Request Failed", e?.error?.detail || "Could not apply overrides.");
                this.savingOverrides.set(false);
                throw e;
            }
        });
    }

    // --- All overrides ---

    openAllOverrides() {
        const customerId = this.customerId();
        if (!customerId) return;

        this.globalService.showWaitOverlay();
        this.apiService.getAllDcOverrides(customerId).subscribe({
            next: overrides => {
                this.allOverrides.set(overrides);
                this.globalService.hideWaitOverlay();
                this.allOverridesModalRef = this.modalService.open(this.allOverridesModal, {
                    modalDialogClass: "modal-dialog-centered modal-xl",
                    backdrop: "static"
                });
            },
            error: () => {
                this.toastService.showDanger("Request Failed", "Failed to load overrides.");
                this.globalService.hideWaitOverlay();
            }
        });
    }

    revertOverride(o: DcOverride) {
        const customerId = this.customerId();
        if (!customerId) return;

        this.modalConfirmComponent.open(
            "Revert this override to the partner map?",
            `"${o.distributionCenterName}" : ${o.zaCode}`);
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                this.apiService.deleteDcOverride(customerId, o.distributionCenterId, o.zaCode).subscribe({
                    next: () => {
                        this.allOverrides.set(this.allOverrides().filter(x => x.id !== o.id));
                        this.toastService.showStandard("Success", "Override reverted.");
                        this.refreshIndicators();
                    },
                    error: (e) => {
                        this.toastService.showDanger("Request Failed", "Could not revert the override.");
                        throw e;
                    }
                });
            },
            () => {
            });
    }

    revertAllForDc(indicator: DcOverrideIndicator) {
        const customerId = this.customerId();
        if (!customerId) return;

        this.modalConfirmComponent.open(
            "Revert all overrides for this distribution center?",
            `"${indicator.distributionCenterName}" (${indicator.overrideCount} codes)`);
        this.modalConfirmComponent.modalRef.result.then(
            () => {
                this.apiService.deleteDcOverride(customerId, indicator.distributionCenterId).subscribe({
                    next: () => {
                        this.toastService.showStandard("Success", "Overrides reverted.");
                        this.refreshIndicators();
                    },
                    error: (e) => {
                        this.toastService.showDanger("Request Failed", "Could not revert the overrides.");
                        throw e;
                    }
                });
            },
            () => {
            });
    }

    private refreshIndicators() {
        const customerId = this.customerId();
        const tradingPartnerId = this.tradingPartnerId();
        if (!customerId || !tradingPartnerId) return;
        this.apiService.getDcOverrideIndicators(customerId, tradingPartnerId)
            .subscribe(indicators => this.overrideIndicators.set(indicators));
    }

    // ISO date (yyyy-mm-ddT...) or yyyy-mm-dd → the value shape an <input type="date"> expects.
    private toDateInput(value: string | null | undefined): string {
        return value ? value.substring(0, 10) : '';
    }
}
